/*
 * Moonchild — global UI behaviors
 * Single source of truth for reveal-on-scroll. Replaces 25+ duplicated
 * IntersectionObserver implementations across sections.
 */

(function () {
  'use strict';

  var revealObserver = null;
  var prefersReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    initReveals(document);
    initCounters(document);
    initBuyQuantity(document);
    initPurchaseOptions(document);
    initProblemInfographics(document);
    initPdpReviews(document);
    initPdpAnalytics(document);
    window.setTimeout(revealHashTarget, 120);

    // Re-scan on Shopify theme editor section reload
    document.addEventListener('shopify:section:load', rescan);
    document.addEventListener('shopify:section:reorder', rescan);
    window.addEventListener('hashchange', revealHashTarget);
  }

  function rescan(event) {
    var scope = event && event.target ? event.target : document;
    initReveals(scope);
    initCounters(scope);
    initBuyQuantity(scope);
    initPurchaseOptions(scope);
    initProblemInfographics(scope);
    initPdpReviews(scope);
    initPdpAnalytics(scope);
  }

  function trackPdpEvent(name, detail) {
    detail = detail || {};

    window.dispatchEvent(new CustomEvent(name, { detail: detail }));

    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: name }, detail));
    }

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try {
        window.Shopify.analytics.publish(name, detail);
      } catch (err) {
        // Shopify's analytics bridge may reject custom events in some previews.
      }
    }
  }

  function initReveals(scope) {
    scope = scope || document;
    prepareRevealGroups(scope);

    var items = scope.querySelectorAll('.mc-reveal:not(.is-visible)');
    if (!items.length) return;

    if (prefersReduce || !('IntersectionObserver' in window)) {
      items.forEach(revealElement);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            revealElement(entry.target);
            revealObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -48px 0px'
      });
    }

    items.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  function prepareRevealGroups(scope) {
    scope.querySelectorAll('[data-mc-reveal-group]').forEach(function (group) {
      group.querySelectorAll('.mc-reveal').forEach(function (el, index) {
        if (!el.style.getPropertyValue('--mc-reveal-index')) {
          el.style.setProperty('--mc-reveal-index', index);
        }
      });
    });
  }

  function revealElement(el) {
    el.classList.add('is-visible');
  }

  function revealHashTarget() {
    if (!window.location.hash || window.location.hash.length < 2) return;
    var id = window.location.hash.slice(1);
    var target;

    try {
      target = document.getElementById(decodeURIComponent(id));
    } catch (err) {
      target = document.getElementById(id);
    }

    if (!target) return;
    if (target.classList && target.classList.contains('mc-reveal')) {
      revealElement(target);
    }
    target.querySelectorAll('.mc-reveal').forEach(revealElement);
  }

  function initPurchaseOptions(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-mc-purchase-options]').forEach(function (root) {
      if (root.dataset.mcPurchaseOptionsReady === 'true') return;
      root.dataset.mcPurchaseOptionsReady = 'true';

      var hiddenInput = root.querySelector('[data-mc-selling-plan-input]');
      var fallback = root.querySelector('[data-mc-selling-plan-fallback]');
      var rechargeMount = root.querySelector('[data-mc-recharge-widget]');

      function syncSelectedPlan() {
        if (!hiddenInput) return;
        var checked = root.querySelector('input[name="mc_purchase_option"]:checked');
        if (checked) {
          hiddenInput.value = checked.dataset.sellingPlanId || '';
        }
      }

      function syncRechargeMount() {
        if (!fallback || !hiddenInput || !rechargeMount) return;
        var hasRechargeWidget = rechargeMount.children.length > 0 || rechargeMount.textContent.trim().length > 0;
        fallback.hidden = hasRechargeWidget;
        hiddenInput.disabled = hasRechargeWidget;
      }

      root.addEventListener('change', function (event) {
        if (event.target && event.target.name === 'mc_purchase_option') {
          syncSelectedPlan();
        }
      });

      syncSelectedPlan();
      syncRechargeMount();

      if (rechargeMount && 'MutationObserver' in window) {
        var observer = new MutationObserver(syncRechargeMount);
        observer.observe(rechargeMount, { childList: true, subtree: true, characterData: true });
      }
    });
  }

  /*
   * Buy box (design system BuyBundles + Notice), shared by every page that
   * renders snippets/buy-buttons.liquid. Cards are radios built in Liquid from
   * the product's selling plans; every price string comes from their data
   * attributes. This keeps the form's id / selling_plan inputs, .is-active,
   * the hint and the sticky bars in step with the choice, writes the Notice
   * from the cart's events and sets aria-busy while the form posts.
   * On every change it dispatches "mc:buy-change" on the form with
   * { price, compare, label, nights, available, offerType } for page-level
   * listeners (PDP v2's sticky bar).
   */
  function initBuyQuantity(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-mc-buy-quantity]').forEach(function (root) {
      if (root.dataset.mcBuyQuantityReady === 'true') return;
      root.dataset.mcBuyQuantityReady = 'true';

      var form = root.closest('form');
      if (!form) return;
      var productForm = root.closest('product-form');
      var radios = Array.prototype.slice.call(root.querySelectorAll('[data-mc-plan]'));
      var tryOnce = root.querySelector('[data-mc-try-once]');
      var tryOnceMode = root.dataset.mcTryOnceMode === 'select' ? 'select' : 'add';
      var hint = root.querySelector('[data-mc-buy-hint]');
      var hintTemplate = hint ? hint.dataset.template || '' : '';
      var variantInput = form.querySelector('[data-mc-primary-variant-input]');
      var quantityInput = form.querySelector('[data-mc-quantity-input]');
      var sellingPlanInput = form.querySelector('[data-mc-selling-plan-input]');
      var submitButton = form.querySelector('[data-mc-buy-submit]');
      var submitText = submitButton ? submitButton.querySelector('span') : null;
      var stickyBar = document.querySelector('[data-mc-sticky-buy-bar]');
      var stickyPrice = stickyBar ? stickyBar.querySelector('.mc-sbb__price strong') : null;
      var stickyCompare = stickyBar ? stickyBar.querySelector('.mc-sbb__price del') : null;
      var onceSelected = false;

      function current() {
        if (onceSelected && tryOnce) return tryOnce;
        for (var i = 0; i < radios.length; i++) {
          if (radios[i].checked) return radios[i];
        }
        return null;
      }

      function sync() {
        var option = current();
        if (!option) return;
        var offerType = option === tryOnce ? 'one-time' : 'subscribe';

        radios.forEach(function (radio) {
          var card = radio.closest('.mc-buy-bundles__card');
          if (card) card.classList.toggle('is-active', radio.checked && !onceSelected);
        });
        if (tryOnce) {
          tryOnce.classList.toggle('is-active', onceSelected);
          tryOnce.setAttribute('aria-pressed', onceSelected ? 'true' : 'false');
        }

        if (variantInput) variantInput.value = option.dataset.variantId;
        // Each bundle is its own variant, so the cart line is always quantity 1.
        if (quantityInput) quantityInput.value = '1';
        if (sellingPlanInput) {
          sellingPlanInput.value = option.dataset.sellingPlan || '';
          sellingPlanInput.disabled = !option.dataset.sellingPlan;
        }

        if (hint && hintTemplate) {
          hint.hidden = onceSelected;
          if (!onceSelected) hint.textContent = hintTemplate.replace('[nights]', option.dataset.nights || '');
        }

        var available = !option.disabled;
        if (submitButton) {
          submitButton.disabled = !available;
          if (submitText) submitText.textContent = available ? submitButton.dataset.cta : submitButton.dataset.soldOut;
        }

        if (stickyPrice && option.dataset.price) stickyPrice.textContent = option.dataset.price;
        if (stickyCompare) {
          stickyCompare.textContent = option.dataset.compare || '';
          stickyCompare.hidden = !option.dataset.compare;
        }

        if (productForm) {
          productForm.dataset.mcSelectedBundlePrice = option.dataset.price || '';
          productForm.dataset.mcSelectedBundleQuantity = option.dataset.quantityValue || '1';
          productForm.dataset.mcSelectedOfferType = offerType;
        }

        form.dispatchEvent(new CustomEvent('mc:buy-change', {
          bubbles: true,
          detail: {
            price: option.dataset.price || '',
            compare: option.dataset.compare || '',
            label: option.dataset.label || '',
            nights: option.dataset.nights || '',
            available: available,
            offerType: offerType
          }
        }));
      }

      radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          onceSelected = false;
          sync();
          trackPdpEvent('pdp_bundle_selected', {
            bundle_size: radio.dataset.quantityValue || '1',
            offer_type: 'subscribe'
          });
        });
      });

      if (tryOnce) {
        tryOnce.addEventListener('click', function () {
          if (tryOnceMode === 'select' && onceSelected) {
            // Second press returns to the plan that was selected on load.
            onceSelected = false;
            var fallback = radios.filter(function (r) { return r.defaultChecked; })[0] || radios[0];
            if (fallback) fallback.checked = true;
            sync();
            return;
          }
          onceSelected = true;
          radios.forEach(function (r) { r.checked = false; });
          sync();
          trackPdpEvent('pdp_bundle_selected', { bundle_size: '1', offer_type: 'one-time' });

          // "add" mode: add the single jar to the cart at once, like the CTA.
          if (tryOnceMode === 'add' && submitButton && !submitButton.disabled && submitButton.getAttribute('aria-disabled') !== 'true') {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitButton);
            } else {
              submitButton.click();
            }
          }
        });
      }

      initBuyStatus(form);
      sync();
    });

    initSpecLinks(scope);
  }

  /* Notice above the CTA and aria-busy on every submit button of the form
     (including a sticky bar's button that points at it with form=). */
  function initBuyStatus(form) {
    if (form.dataset.mcBuyStatusReady === 'true') return;
    form.dataset.mcBuyStatusReady = 'true';

    var notice = form.querySelector('[data-mc-buy-notice]');
    var ICONS = {
      success: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.5 2.5L16 9.5"/></svg>',
      alert: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5h.01"/></svg>'
    };

    function submitButtons() {
      var buttons = Array.prototype.slice.call(form.querySelectorAll('[type="submit"]'));
      // getAttribute: the form holds an input named "id", which shadows form.id.
      var formId = form.getAttribute('id');
      if (formId) {
        document.querySelectorAll('[type="submit"][form="' + formId + '"]').forEach(function (b) { buttons.push(b); });
      }
      return buttons;
    }

    var busyTimer = null;
    function setBusy(busy) {
      submitButtons().forEach(function (button) {
        if (busy) {
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-busy');
        }
      });
      window.clearTimeout(busyTimer);
      if (busy) busyTimer = window.setTimeout(function () { setBusy(false); }, 15000);
    }

    function showNotice(kind, text, withAction) {
      if (!notice) return;
      notice.classList.remove('mc-notice--success', 'mc-notice--alert');
      notice.classList.add('mc-notice--' + kind);
      notice.setAttribute('role', kind === 'alert' ? 'alert' : 'status');
      notice.querySelector('[data-mc-buy-notice-icon]').innerHTML = ICONS[kind];
      notice.querySelector('[data-mc-buy-notice-body]').textContent = text;
      notice.querySelector('[data-mc-buy-notice-action]').hidden = !withAction;
      notice.hidden = false;
    }

    form.addEventListener('submit', function () {
      if (notice) notice.hidden = true;
      setBusy(true);
    }, true);

    // Dawn's pubsub.js / constants.js declare these at the top level (PUB_SUB_EVENTS is a const, not a window property).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      var variantIdOf = function () {
        var input = form.querySelector('[name="id"]');
        return input ? String(input.value) : '';
      };
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (!event || event.source !== 'product-form' || String(event.productVariantId) !== variantIdOf()) return;
        setBusy(false);
        showNotice('success', notice ? notice.dataset.added : '', true);
      });
      subscribe(PUB_SUB_EVENTS.cartError, function (event) {
        if (!event || event.source !== 'product-form' || String(event.productVariantId) !== variantIdOf()) return;
        setBusy(false);
        // Shopify's description is a full sentence for limits ("You can only add 5 ... to the cart.");
        // anything else (a bare "Cart Error", "Cannot find variant") gets the brand's line instead.
        var description = typeof event.errors === 'string' ? event.errors.trim() : '';
        showNotice('alert', /\.$/.test(description) ? description : (notice ? notice.dataset.error : ''), false);
      });
    }
  }

  /* Supplement-facts links open the <details> panel they point at. */
  function initSpecLinks(scope) {
    (scope || document).querySelectorAll('[data-mc-spec-link]').forEach(function (link) {
      if (link.dataset.mcSpecReady === 'true') return;
      link.dataset.mcSpecReady = 'true';
      link.addEventListener('click', function () {
        var href = link.getAttribute('href') || '';
        if (href.charAt(0) !== '#') return;
        var target = document.getElementById(href.slice(1));
        if (target && target.tagName === 'DETAILS') target.open = true;
      });
    });
  }

  function initProblemInfographics(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-mc-problem-section]').forEach(function (root) {
      if (root.dataset.mcProblemReady === 'true') return;
      root.dataset.mcProblemReady = 'true';

      var triggers = root.querySelectorAll('[data-mc-problem-trigger]');
      var panels = root.querySelectorAll('[data-mc-problem-panel]');
      var tabs = root.querySelectorAll('[data-mc-problem-tab]');
      if (!triggers.length || !panels.length) return;

      function selectProblem(key) {
        root.dataset.mcProblemActive = key;

        triggers.forEach(function (trigger) {
          var active = trigger.dataset.mcProblemTrigger === key;
          trigger.classList.toggle('is-active', active);
          trigger.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        panels.forEach(function (panel) {
          var active = panel.dataset.mcProblemPanel === key;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        tabs.forEach(function (tab) {
          tab.classList.toggle('is-active', tab.dataset.mcProblemTab === key);
        });
      }

      triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          selectProblem(trigger.dataset.mcProblemTrigger || 'moonchild');
        });
      });

      var checked = root.querySelector('[data-mc-problem-trigger].is-active');
      selectProblem((checked && checked.dataset.mcProblemTrigger) || triggers[0].dataset.mcProblemTrigger || 'moonchild');
    });
  }

  function initPdpReviews(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-mc-pdp-reviews]').forEach(function (root) {
      if (root.dataset.mcReviewsReady === 'true') return;
      root.dataset.mcReviewsReady = 'true';

      var filters = root.querySelectorAll('[data-mc-review-filter]');
      var items = root.querySelectorAll('[data-mc-review-item]');
      var rails = root.querySelectorAll('.mc-pdp-reviews__quotes, .mc-pdp-reviews__media');
      if (!filters.length || !items.length) return;

      filters.forEach(function (filter) {
        filter.addEventListener('click', function () {
          var active = filter.dataset.mcReviewFilter || 'all';

          filters.forEach(function (button) {
            button.setAttribute('aria-pressed', button === filter ? 'true' : 'false');
          });

          items.forEach(function (item) {
            var show = active === 'all' || item.dataset.category === active;
            item.classList.toggle('mc-pdp-reviews__empty', !show);
          });

          rails.forEach(function (rail) {
            rail.scrollLeft = 0;
          });

          trackPdpEvent('pdp_review_filter_clicked', {
            filter: active
          });
        });
      });
    });
  }

  function initPdpAnalytics(scope) {
    scope = scope || document;
    initPdpSectionTracking(scope);
    initPdpFaqTracking(scope);
    initPdpScrollCtas(scope);
  }

  function initPdpSectionTracking(scope) {
    var sections = scope.querySelectorAll('section[id]:not([id^="shopify-section-"]), #buy-box');
    if (!sections.length) return;

    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (section) {
        if (section.dataset.mcSectionTracked === 'true') return;
        section.dataset.mcSectionTracked = 'true';
        trackPdpEvent('pdp_section_in_view', { section_name: section.id || 'buy-box' });
      });
      return;
    }

    if (!window.mcPdpSectionObserver) {
      window.mcPdpSectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.target.dataset.mcSectionTracked === 'true') return;
          entry.target.dataset.mcSectionTracked = 'true';
          window.mcPdpSectionObserver.unobserve(entry.target);
          trackPdpEvent('pdp_section_in_view', {
            section_name: entry.target.id || 'buy-box'
          });
        });
      }, {
        threshold: 0.3,
        rootMargin: '0px 0px -20% 0px'
      });
    }

    sections.forEach(function (section) {
      if (section.dataset.mcSectionTrackingReady === 'true' || section.dataset.mcSectionTracked === 'true') return;
      section.dataset.mcSectionTrackingReady = 'true';
      window.mcPdpSectionObserver.observe(section);
    });
  }

  function initPdpFaqTracking(scope) {
    scope.querySelectorAll('.mc-pdp-faq__item').forEach(function (item, index) {
      if (item.dataset.mcFaqTrackingReady === 'true') return;
      item.dataset.mcFaqTrackingReady = 'true';

      item.addEventListener('toggle', function () {
        if (!item.open) return;
        var question = item.querySelector('.mc-pdp-faq__summary span');
        trackPdpEvent('pdp_faq_opened', {
          question_id: 'q' + (index + 1),
          question: question ? question.textContent.trim() : ''
        });
      });
    });
  }

  function initPdpScrollCtas(scope) {
    scope.querySelectorAll('[data-mc-scroll-cta], [data-mc-scroll-to-buy-box]').forEach(function (cta) {
      if (cta.dataset.mcScrollTrackingReady === 'true') return;
      cta.dataset.mcScrollTrackingReady = 'true';

      cta.addEventListener('click', function () {
        var position = cta.dataset.mcCtaPosition || 'unknown';
        if (cta.closest('[data-mc-sticky-buy-bar]')) position = 'sticky-mobile';
        trackPdpEvent('pdp_scroll_anchor_cta_clicked', {
          cta_position: position,
          href: cta.getAttribute('href') || '#buy-box'
        });
      });
    });
  }

  /* Stat counter animation —
     <span data-mc-counter data-target="23" data-prefix="+" data-suffix=" min">0</span>
     Animates from 0 to data-target over ~1200ms when in view. */
  function initCounters(scope) {
    scope = scope || document;
    if (!('IntersectionObserver' in window)) {
      scope.querySelectorAll('[data-mc-counter]').forEach(function (el) {
        renderCounter(el, parseFloat(el.dataset.target));
      });
      return;
    }
    var prefersReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var target = parseFloat(el.dataset.target);
          if (prefersReduce) {
            renderCounter(el, target);
          } else {
            animateCounter(el, target);
          }
          io.unobserve(el);
        }
      });
    }, { threshold: 0.4 });
    scope.querySelectorAll('[data-mc-counter]').forEach(function (el) { io.observe(el); });
  }

  function renderCounter(el, value) {
    var prefix = el.dataset.prefix || '';
    var suffix = el.dataset.suffix || '';
    var decimals = parseInt(el.dataset.decimals || '0', 10);
    el.textContent = prefix + value.toFixed(decimals) + suffix;
  }

  function animateCounter(el, target) {
    var prefix = el.dataset.prefix || '';
    var suffix = el.dataset.suffix || '';
    var decimals = parseInt(el.dataset.decimals || '0', 10);
    var duration = parseInt(el.dataset.duration || '1400', 10);
    var startTime = null;

    function step(t) {
      if (!startTime) startTime = t;
      var elapsed = t - startTime;
      var progress = Math.min(1, elapsed / duration);
      // easeOutCubic
      var eased = 1 - Math.pow(1 - progress, 3);
      var value = target * eased;
      el.textContent = prefix + value.toFixed(decimals) + suffix;
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
